import { gql } from "@apollo/client";
import { PortalProvider } from "@blueprintjs/core";
import isPropValid from "@emotion/is-prop-valid";
import {
  AppTheme,
  HexId,
  HexLocator,
  HexVersionString,
  getCustomFontUrl,
  getHexId,
  randomString,
} from "@hex/common";
import React, { useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  ShouldForwardProp,
  StyleSheetManager,
  ThemeProvider,
} from "styled-components";

import { useGetThemeTypeWithPreference } from "./hooks/useGetThemeFromName";
import { LocalStorageKeys, useLocalStorage } from "./hooks/useLocalStorage";
import { McpThemeContext } from "./mcp-apps-sdk/mcpTheme";
import {
  useIsSignedEmbeddingApp,
  useThemeOverride,
} from "./route/routeHooks.js";
import { Routes } from "./route/routes";
import { Theme } from "./theme/common/theme.js";
import {
  CssVariableInjector,
  CustomStylesOverrides,
} from "./theme/customGlobalThemeResets";
import { getCustomTheme } from "./theme/customThemes.js";
import { DARK_THEME } from "./theme/themes/darkTheme.js";
import { LIGHT_THEME } from "./theme/themes/lightTheme.js";
import {
  OrgPublishedAppThemeQueryFragment,
  useGetHexVersionQuery,
} from "./ThemeWrapper.generated";

gql`
  fragment OrgCustomFontFragment on OrgCustomFont {
    id
    name
    fontDescriptor
  }

  fragment OrgPublishedAppThemeQueryFragment on OrgPublishedAppTheme {
    id
    name
    colorPalette {
      id
      colors
      name
    }
    font {
      id
      ...OrgCustomFontFragment
    }
    styles
  }

  query GetHexVersion($hexId: HexId!, $version: HexVersionString!) {
    hexVersionByNumber(hexId: $hexId, version: $version) {
      id
      appTheme
      customPublishedAppTheme {
        id
        ...OrgPublishedAppThemeQueryFragment
      }
    }
  }
`;

export const ThemeOptionContext = React.createContext<{
  appTheme: Theme;
  baseTheme: Theme;
  refetch: () => Promise<unknown>;
  customPortalClassName: string;
} | null>(null);

export const CustomFontContext = React.createContext<{
  type: "google";
  url: string;
} | null>(null);

/**
 * This is exclusively used by the GridLayoutDisplay.tsx component
 * This lets us match a custom portal provider in a subtree to the same portal as it's
 * closest parent ThemeOverrideWrapper
 */
export const PortalClassNameContext = React.createContext<string | undefined>(
  undefined,
);

/**
 * A react provider that allows sub elements to opt in and opt out of the base theme and or custom published app theme
 * This does not load themes from hexVersions, just toggles between custom and base themes
 *
 * If using ThemeOverrideWrapper, will need to inject ThemeResetVariables to the child div to set override css variables
 */
export const ThemeOverrideWrapper: React.FC<
  React.PropsWithChildren<{
    theme: "app" | "base";
  }>
> = React.memo(function ThemeOverrideWrapper({ children, theme }) {
  const themeOptions = useContext(ThemeOptionContext);

  if (!themeOptions) {
    return <>{children}</>;
  }

  if (theme === "app") {
    return (
      <ThemeProvider theme={themeOptions.appTheme}>
        <PortalClassNameContext value={themeOptions.customPortalClassName}>
          <PortalProvider portalClassName={themeOptions.customPortalClassName}>
            {children}
          </PortalProvider>
        </PortalClassNameContext>
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={themeOptions.baseTheme}>
        <PortalClassNameContext value={undefined}>
          <PortalProvider portalClassName={undefined}>
            {children}
          </PortalProvider>
        </PortalClassNameContext>
      </ThemeProvider>
    );
  }
});

export const SignedEmbeddingAppThemeOverrideWrapper = React.memo(
  function SignedEmbeddingAppThemeOverrideWrapper(props: {
    children?: React.ReactNode;
  }) {
    const isSignedEmbeddingApp = useIsSignedEmbeddingApp();
    return (
      <ThemeOverrideWrapper
        theme={isSignedEmbeddingApp ? "app" : "base"}
        {...props}
      />
    );
  },
);

interface ThemeWrapperProps {
  customThemeOverride?: Pick<
    OrgPublishedAppThemeQueryFragment,
    "styles" | "font" | "colorPalette"
  >;
}

const FontLoader = React.memo(function FontLoader({
  customTheme,
}: {
  customTheme?: Pick<
    OrgPublishedAppThemeQueryFragment,
    "styles" | "font" | "colorPalette"
  > | null;
}) {
  const urlToLoad: string | null = useMemo(() => {
    if (customTheme == null) {
      return null;
    }

    return getCustomFontUrl(
      customTheme.styles.font,
      customTheme.font?.fontDescriptor,
    );
  }, [customTheme]);

  return urlToLoad ? <link href={urlToLoad} rel="stylesheet" /> : null;
});

interface AppRouteParams {
  hexLocator: HexLocator;
  version: HexVersionString;
}

interface NotebookRouteParams {
  hexLocator: HexLocator;
  version?: HexVersionString;
}

const useHexVersionFromRoute = (): {
  hexId: HexId;
  version: HexVersionString;
  context: "app" | "notebook";
} | null => {
  const appRouteMatch = useMatch<keyof AppRouteParams, string>(Routes.APP);
  const appRouteParams = appRouteMatch
    ? (appRouteMatch.params as AppRouteParams)
    : null;
  const notebookRouteMatch = useMatch<keyof NotebookRouteParams, string>(
    Routes.LOGIC,
  );
  const notebookRouteParams = notebookRouteMatch
    ? (notebookRouteMatch.params as NotebookRouteParams)
    : null;
  // Project-thread URLs use HEX_BASE while still needing notebook theme context.
  const projectRouteMatch = useMatch<keyof NotebookRouteParams, string>(
    Routes.HEX_BASE,
  );
  const projectRouteParams = projectRouteMatch
    ? (projectRouteMatch.params as NotebookRouteParams)
    : null;
  const maybeHexIdOrHumanHexId =
    appRouteParams?.hexLocator ??
    notebookRouteParams?.hexLocator ??
    projectRouteParams?.hexLocator;
  let hexId = null;
  if (maybeHexIdOrHumanHexId != null) {
    hexId = getHexId(maybeHexIdOrHumanHexId);
  }

  if (appRouteParams != null && hexId != null) {
    return {
      hexId,
      version: appRouteParams.version,
      context: "app",
    };
  } else if (notebookRouteMatch != null && hexId != null) {
    return {
      hexId,
      version: notebookRouteParams?.version ?? "draft",
      context: "notebook",
    };
  } else if (projectRouteMatch != null && hexId != null) {
    return {
      hexId,
      version: projectRouteParams?.version ?? "draft",
      context: "notebook",
    };
  } else {
    return null;
  }
};

const useQueryHexVersionTheme = (
  customThemeOverride?: Pick<
    OrgPublishedAppThemeQueryFragment,
    "styles" | "font" | "colorPalette"
  >,
): {
  hexVersionAppTheme: AppTheme | null;
  customHexVersionTheme: Pick<
    OrgPublishedAppThemeQueryFragment,
    "styles" | "font" | "colorPalette"
  > | null;
  context: "app" | "notebook" | null;
  refetch: () => Promise<unknown>;
  loading: boolean;
} => {
  const hexVersionFromRoute = useHexVersionFromRoute();
  const hexId: HexId = hexVersionFromRoute?.hexId ?? ("" as HexId);
  const version: HexVersionString =
    hexVersionFromRoute?.version ?? ("" as HexVersionString);

  const { data, loading, refetch } = useGetHexVersionQuery({
    skip: hexVersionFromRoute == null || !!customThemeOverride,
    variables: {
      hexId,
      version,
    },
  });
  const hexVersionAppTheme = data?.hexVersionByNumber.appTheme ?? null;
  const customHexVersionTheme =
    customThemeOverride ??
    data?.hexVersionByNumber.customPublishedAppTheme ??
    null;

  return {
    hexVersionAppTheme,
    customHexVersionTheme,
    refetch,
    context: hexVersionFromRoute?.context ?? null,
    loading,
  };
};

const useThemeName = (): Exclude<AppTheme, "SYS_PREF"> => {
  const [userThemeName] = useLocalStorage(LocalStorageKeys.THEME_NAME);

  const resolvedTheme = useThemeOverride() ?? userThemeName;
  const baseThemeType = useGetThemeTypeWithPreference(resolvedTheme);

  return baseThemeType;
};

const ThemeWrapper: React.FunctionComponent<
  React.PropsWithChildren<ThemeWrapperProps>
> = ({ children, customThemeOverride }) => {
  // If we render in an MCP theme, we use the mcp theme as the base theme
  const mcpTheme = useContext(McpThemeContext);
  const isSignedEmbeddedApp = useIsSignedEmbeddingApp();
  const {
    context,
    customHexVersionTheme,
    hexVersionAppTheme,
    loading,
    refetch,
  } = useQueryHexVersionTheme(customThemeOverride);
  const customPortalClassName = useMemo(
    () => `nested-portal-reset-${randomString(5)}`,
    [],
  );
  const baseThemeName = useThemeName();
  // When we use a custom theme, we want to use the custom theme's inferred light or dark mode to choose the Hex wrapper's theme around the published app
  // We only want to do this in the published app view.
  const computedThemes = useMemo(() => {
    const baseTheme =
      mcpTheme ?? (baseThemeName === "DARK" ? DARK_THEME : LIGHT_THEME);
    const maybeCustomTheme = customHexVersionTheme
      ? getCustomTheme(
          customHexVersionTheme,
          baseThemeName === "DARK" ? "dark" : "light",
        )
      : null;

    if (maybeCustomTheme) {
      return {
        appTheme: maybeCustomTheme.customTheme,
        baseTheme:
          context === "app" || customThemeOverride
            ? maybeCustomTheme.isLight
              ? LIGHT_THEME
              : DARK_THEME
            : baseTheme,
      };
    } else if (hexVersionAppTheme && hexVersionAppTheme !== "SYS_PREF") {
      const appTheme = hexVersionAppTheme === "DARK" ? DARK_THEME : LIGHT_THEME;
      return {
        appTheme: appTheme,
        // If we're viewing a published app, we should see the app theme everywhere, otherwise we use the user's theme as the base layer
        baseTheme: context === "app" ? appTheme : baseTheme,
      };
    } else {
      return {
        appTheme: baseTheme,
        baseTheme: baseTheme,
      };
    }
  }, [
    baseThemeName,
    context,
    customHexVersionTheme,
    customThemeOverride,
    hexVersionAppTheme,
    mcpTheme,
  ]);

  const themeOptionContextValue = useMemo(() => {
    return {
      appTheme: computedThemes.appTheme,
      baseTheme: computedThemes.baseTheme,
      refetch,
      customPortalClassName,
    };
  }, [computedThemes, refetch, customPortalClassName]);

  const customFontValue = useMemo(() => {
    if (customHexVersionTheme == null) {
      return null;
    }

    const googleFontUrl = getCustomFontUrl(
      customHexVersionTheme.styles.font,
      customHexVersionTheme.font?.fontDescriptor,
    );
    if (googleFontUrl) {
      return {
        type: "google" as const,
        url: googleFontUrl,
      };
    } else {
      return null;
    }
  }, [customHexVersionTheme]);

  // If there is an error, we'll fallback to a hex theme
  if (loading) {
    return null;
  }

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      {/**
       * This sets the theme for all components that don't specifically set the theme
       * via ThemeOverrideWrapper
       */}
      <ThemeProvider
        theme={
          isSignedEmbeddedApp
            ? computedThemes.appTheme
            : computedThemes.baseTheme
        }
      >
        {/**
         * This makes both app and base theme available to child <ThemeOverrideWrapper/>s
         */}
        <ThemeOptionContext value={themeOptionContextValue}>
          <CustomFontContext value={customFontValue}>
            <FontLoader customTheme={customHexVersionTheme} />
            {/*
            We skip global style injection when using a custom theme override, for some reason
            styled component global injection is incredibly slow and we don't need custom css styles
            when passing a customHexThemeOverride as they're called multiple times in the same app
          */}
            {!customThemeOverride && (
              <>
                <CssVariableInjector
                  theme={
                    isSignedEmbeddedApp
                      ? computedThemes.appTheme
                      : computedThemes.baseTheme
                  }
                />
                {/**
                 * CustomStylesOverrides sets themes for vega tooltips,
                 * and adds some global stylesheets that will match
                 * any popovers that get rendered inside
                 * <ThemeOverrideWrapper theme="app" />
                 */}
                <CustomStylesOverrides
                  className={customPortalClassName}
                  // This only takes effect for components
                  // wrapped with <ThemeOverrideWrapper theme="app"/>
                  // so this should always apply the app theme
                  theme={computedThemes.appTheme}
                />
              </>
            )}
            {children}
          </CustomFontContext>
        </ThemeOptionContext>
      </ThemeProvider>
    </StyleSheetManager>
  );
};

// This implements the default behavior from styled-components v5
const shouldForwardProp: ShouldForwardProp<"web"> = (propName, target) => {
  if (typeof target === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
};

export default ThemeWrapper;
